import { ContactAppUser, ContactOwner, Tag } from "./contacts";

export interface Company {
  uuid: string;
  name: string;
  organisationUuid: string;
  organisationName: string;
  industry?: string;
  cityLocation?: string;
  companyKey?: string;
  websiteUrl?: string;
  phone?: string;
  linkedInUrl?: string;
  appUser?: ContactAppUser;
  description?: string;
  tags?: Tag[];
  imageUrl: string | null;
  createdAt:string;
  lastActivityAt:string
  owner:ContactOwner
  countryCode:string;
  phoneNumber:string; 
  overdueTasks:number
  dealValue:number
  activitiesCount:number
  notesCount:number
  tasksCount:number
}

/**
 * Minimum shape a company list row needs. Both `Company` and the
 * deal-association company payload satisfy it.
 */
export type CompanyRowData = Pick<Company, "uuid" | "name" | "companyKey"> &
  Partial<Pick<Company, "industry" | "cityLocation">>;

export interface CompaniesPage {
  content: Company[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
}

// Matches the envelope shape used across the CRM API
// (success/status/statusCode/message/data/error/meta)
export interface CompaniesResponse {
  success: boolean;
  status: string;
  statusCode: number;
  message: string;
  data: CompaniesPage;
}

export interface CompanyDetailResponse {
  success: boolean;
  status: string;
  statusCode: number;
  message: string;
  data: Company;
}

export interface GetCompaniesParams {
  search?: string;
  page?: number;
  size?: number;
}


export interface NonAssociatedContactCompanyParams {
  search?: string;
  page?: number;
  limit?: number;
}

/** GET /crm/companies/{companyUuid}/contacts/non-associated */
export interface NonAssociatedCompanyContactParams {
  companyUuid: string;
  search?: string;
  page?: number;
  limit?: number;
}

// Payload for POST /crm/contacts/{contactUuid}/companies — creates a company
// and associates it with the contact in one call.
export interface CreateContactCompanyPayload {
  companyName: string;
  industry?: string;
  cityLocation?: string;
  websiteUrl?: string;
  phone?: string;
  appUserUuid?: string;
  linkedInUrl?: string;
  tags?: string[];
  description?: string;
  timezone?: string;
  companyOwnerUuid?:string
}

// Same body as the contact variant — creates a company and associates it with
// the deal in one call.
export type CreateDealCompanyPayload = CreateContactCompanyPayload;

/** Body for POST /crm/companies */
export interface CreateCompanyPayload {
  companyName: string;
  industry?: string;
  cityLocation?: string;
  websiteUrl?: string;
  phone?: string;
  companyOwnerUuid?: string;
  country?: string;
  linkedInUrl?: string;
  tags?: string[];
  description?: string;
  timezone?: string;
  organisationUuid?: string;
  appUserUuid?: string;
}

export interface UpdateCompanyPayload {
  companyUuid: string;
  name?: string;
  industry?: string;
  cityLocation?: string;
  websiteUrl?: string;
  phone?: string;
  linkedInUrl?: string;
  description?: string;
  tags?: string[];
  companyOwnerUuid?: string;
}

export type CompanyOwner = ContactOwner;

export interface CompanyFormValues {
  title: string;
  industry: string;
  city: string;
  website: string;
  phone: string;
  companyOwnerUuid: string;
  linkedin: string;
  tags: string[];
  description: string;
}

export const companyPageInitialValues: CompanyFormValues = {
  title: '',
  industry: '',
  city: '',
  website: '',
  phone: '',
  companyOwnerUuid: '',
  linkedin: '',
  tags: [],
  description: '',
};

/** Fields the create-page checklist tracks — the ones the API treats as mandatory. */
export const COMPANY_PAGE_REQUIRED_FIELDS: {
  key: 'title' | 'industry';
  label: string;
}[] = [
  { key: 'title', label: 'Title' },
  { key: 'industry', label: 'Industry' },
];

export const COMPANY_DESCRIPTION_MAX = 500;

export interface CompaniesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (values: CompanyFormValues) => Promise<void> | void;
  isSaving?: boolean;
  editingData?: Partial<CompanyFormValues> | null;
}

export const INDUSTRY_OPTIONS = [
  'Information Technology',
  'Software & SaaS',
  'Finance & Banking',
  'Healthcare',
  'Manufacturing',
  'Retail & E-commerce',
  'Education',
  'Real Estate',
  'Consulting',
  'Media & Entertainment',
  'Telecommunications',
  'Other',
].map((industry) => ({ value: industry, label: industry }));